/**
 * Cliente da API.
 *
 * Concentra tres coisas que, espalhadas pelas telas, viram bug: o token de
 * acesso, a renovacao automatica quando ele expira, e a traducao do erro da API
 * em algo que a tela possa exibir.
 */

const BASE = '/api/v1';

const ACCESS_KEY = 'dwu.access';
const REFRESH_KEY = 'dwu.refresh';
const TENANT_KEY = 'dwu.tenant';

export interface ApiErrorBody {
  statusCode: number;
  code: string;
  message: string;
  correlationId?: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  readonly details?: unknown;

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = body.statusCode;
    this.code = body.code;
    this.correlationId = body.correlationId;
    this.details = body.details;
  }

  /** Conflito de versao no lock otimista (§54). */
  get isVersionConflict(): boolean {
    return this.status === 409 && /alterado por outro usuario/i.test(this.message);
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

// --- armazenamento da sessao ------------------------------------------------
// localStorage e por navegador e nunca sai dele. Envolvido em try/catch porque
// falha em janela privada e com dados de site bloqueados.

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* sessao apenas em memoria nesta aba */
  }
}

export const session = {
  get accessToken(): string | null {
    return read(ACCESS_KEY);
  },
  get refreshToken(): string | null {
    return read(REFRESH_KEY);
  },
  get tenant(): string | null {
    return read(TENANT_KEY);
  },
  save(tokens: { accessToken: string; refreshToken: string }, tenant?: string): void {
    write(ACCESS_KEY, tokens.accessToken);
    write(REFRESH_KEY, tokens.refreshToken);
    if (tenant) write(TENANT_KEY, tenant);
  },
  clear(): void {
    write(ACCESS_KEY, null);
    write(REFRESH_KEY, null);
  },
};

// --- renovacao de token -----------------------------------------------------

let refreshing: Promise<boolean> | null = null;

/**
 * Renova o par de tokens. Requisicoes concorrentes compartilham a mesma
 * promessa: sem isso, cinco chamadas simultaneas disparariam cinco renovacoes, e
 * a rotacao invalidaria as quatro ultimas — derrubando a sessao do usuario.
 */
async function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;

  const refreshToken = session.refreshToken;
  if (!refreshToken) return false;

  refreshing = (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        session.clear();
        return false;
      }

      const tokens = (await response.json()) as { accessToken: string; refreshToken: string };
      session.save(tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

let onUnauthenticated: (() => void) | null = null;

/** Registrado pelo provedor de autenticacao para redirecionar ao login. */
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

// --- requisicao -------------------------------------------------------------

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Rota publica: nao envia token nem tenta renovar. */
  anonymous?: boolean;
  signal?: AbortSignal;
  /**
   * Chave de idempotencia (ADR-03).
   *
   * Baixa, pagamento, estorno, renegociacao e emissao de NFS-e **exigem** o header,
   * e sem ele a API recusa com 409. A chave deve ser estavel enquanto for a mesma
   * tentativa do operador: gerar uma a cada chamada faria o duplo clique passar,
   * que e exatamente o que o mecanismo existe para impedir.
   */
  idempotencyKey?: string;
}

/**
 * Chave para uma tentativa de operacao.
 *
 * Gerada uma vez pela tela — ao abrir o formulario — e reusada enquanto a tentativa
 * for a mesma. Depois de concluir, a tela descarta e a proxima operacao ganha outra.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

async function request<T>(path: string, options: RequestOptions = {}, retrying = false): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  if (!options.anonymous) {
    const token = session.accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  // Token expirado: renova uma vez e repete. Uma unica tentativa, para nao
  // entrar em laco quando a sessao realmente acabou.
  if (response.status === 401 && !options.anonymous && !retrying) {
    const renewed = await refreshSession();
    if (renewed) return request<T>(path, options, true);

    session.clear();
    onUnauthenticated?.();
    throw new ApiError({
      statusCode: 401,
      code: 'UNAUTHENTICATED',
      message: 'Sua sessao expirou. Entre novamente.',
    });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const body = (payload ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError({
      statusCode: body.statusCode ?? response.status,
      code: body.code ?? 'ERROR',
      message: body.message ?? 'Nao foi possivel completar a operacao.',
      correlationId: body.correlationId,
      details: body.details,
    });
  }

  return payload as T;
}

/**
 * Baixa um arquivo gerado pela API (§38).
 *
 * Nao passa por `request`: aquele le a resposta como texto e faz `JSON.parse`, o que
 * corromperia um XLSX — e o erro apareceria como planilha que nao abre, nao como
 * falha de rede. Aqui a resposta e lida como blob, e o nome do arquivo vem do
 * `Content-Disposition` que o servidor manda.
 *
 * Repete a renovacao de token uma vez, como `request`, senao exportar com a sessao
 * quase expirada baixaria um arquivo com o JSON do erro 401 dentro.
 */
async function downloadFile(
  path: string,
  options: { method: 'GET' | 'POST'; body?: unknown },
  fallbackName: string,
  retrying = false,
): Promise<{ fileName: string; rows: number | null; missing: number | null }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = session.accessToken;
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${BASE}${path}`, {
    method: options.method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 401 && !retrying) {
    const renewed = await refreshSession();
    if (renewed) return downloadFile(path, options, fallbackName, true);

    session.clear();
    onUnauthenticated?.();
    throw new ApiError({
      statusCode: 401,
      code: 'UNAUTHENTICATED',
      message: 'Sua sessao expirou. Entre novamente.',
    });
  }

  if (!response.ok) {
    // O erro vem como JSON mesmo numa rota que devolve arquivo.
    const text = await response.text();
    const payload = (text ? (JSON.parse(text) as Partial<ApiErrorBody>) : {}) ?? {};
    throw new ApiError({
      statusCode: payload.statusCode ?? response.status,
      code: payload.code ?? 'ERROR',
      message: payload.message ?? 'Nao foi possivel exportar.',
      correlationId: payload.correlationId,
      details: payload.details,
    });
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const rows = Number(response.headers.get('X-Export-Rows'));
  // Quantos arquivos faltaram no pacote fiscal: "24 notas e 23 arquivos" e a
  // diferenca que o contador precisa enxergar antes de entregar.
  const missing = Number(response.headers.get('X-Archive-Missing'));

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Sem revogar, o blob fica na memoria da aba ate recarregar — e exportar dez
    // vezes numa tarde deixa dez arquivos presos.
    URL.revokeObjectURL(url);
  }

  return {
    fileName,
    rows: Number.isFinite(rows) ? rows : null,
    missing: Number.isFinite(missing) ? missing : null,
  };
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  /** Baixa um arquivo gerado por `POST`, mandando filtro no corpo (§38). */
  download: (path: string, body: unknown, fallbackName: string) =>
    downloadFile(path, { method: 'POST', body }, fallbackName),
  /** Baixa um arquivo de uma rota `GET`, com os parametros na querystring. */
  downloadGet: (path: string, fallbackName: string) =>
    downloadFile(path, { method: 'GET' }, fallbackName),
  /**
   * O terceiro argumento aceita `AbortSignal` (uso antigo) ou opcoes. Duas formas
   * porque dezenas de chamadas ja passam o sinal, e troca-las todas para acomodar
   * a chave de idempotencia seria mexer no que funciona.
   */
  post: <T>(
    path: string,
    body?: unknown,
    options?: AbortSignal | { signal?: AbortSignal; idempotencyKey?: string },
  ) => {
    const extra =
      options instanceof AbortSignal ? { signal: options } : (options ?? {});
    return request<T>(path, { method: 'POST', body, ...extra });
  },
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  public: {
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'POST', body, anonymous: true }),
  },
};

// --- tipos compartilhados com a API ----------------------------------------

export interface Paginated<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface FieldMeta {
  name: string;
  label: string;
  type:
    | 'string'
    | 'number'
    | 'money'
    | 'boolean'
    | 'date'
    | 'datetime'
    | 'enum'
    | 'uuid'
    | 'json';
  operators: string[];
  sortable: boolean;
  enumValues?: string[];
  isCustomField?: boolean;
}

export interface EntityMeta {
  entity: string;
  defaultSort: { field: string; direction: 'asc' | 'desc' };
  fields: FieldMeta[];
}

export interface AuthenticatedUser {
  id: string;
  tenantId: string;
  tenantSlug: string;
  email: string;
  fullName: string;
  isTenantAdmin: boolean;
  permissions: string[];
  companies: Array<{ companyId: string; branchId: string | null; isDefault: boolean }>;
}

export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  isTenantAdmin: boolean;
  permissions: string[];
  tenant: { id: string; slug: string };
  companyAccess: Array<{
    isDefault: boolean;
    company: { id: string; code: string; tradeName: string | null; legalName: string };
    branch: { id: string; code: string; name: string } | null;
  }>;
}
